import React from 'react';
import { Icon, StatusPill } from '../../campaign-os';
import { checkByKey, CHECK_STATUS_LABEL, FACT_OUTCOME_LABEL } from '../data/selfRegularization';

/**
 * Small pieces the self-regularization screens share: the mock notice, a chip naming a check,
 * and the pills for a check's status and a fact's outcome. Kept together so the three screens
 * say the same thing in the same colours.
 */

/** Same shape as the Wallet's "not connected to billing" notice — the tool's way of flagging a gap. */
function Notice({ icon = 'science', tone = 'beige', title, text }) {
  const box = tone === 'amber' ? 'border-cos-status-amber/30 bg-cos-status-amber-bg/60' : 'border-cos-beige-light bg-cos-beige-light/40';
  const tile = tone === 'amber' ? 'bg-cos-status-amber-bg text-cos-status-amber' : 'bg-cos-beige-light text-cos-beige-ink';
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-3 py-2 ${box}`}>
      <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-lg ${tile}`}>
        <Icon name={icon} className="text-[14px]" />
      </span>
      <div className="min-w-0">
        <p className="text-[12px] font-bold text-cos-ink">{title}</p>
        {text && <p className="mt-0.5 text-[12px] leading-[17px] text-cos-ink-secondary">{text}</p>}
      </div>
    </div>
  );
}

/**
 * What the numbers on this screen are worth, read from the server's own configuration: whether
 * the registry answering is the mock, and whether prices are the real rate card. Nothing shows on a
 * fully configured deployment. `provider` may be passed for a run that has already recorded who
 * answered it.
 */
export function RegistryNotice({ status, provider }) {
  const mock = provider ? provider === 'mock' : status?.mock;
  const items = [];
  if (status?.providerProblem) {
    items.push({ key: 'problem', icon: 'error', tone: 'amber', title: 'The registry is not configured', text: status.providerProblem });
  } else if (mock) {
    items.push({
      key: 'mock',
      icon: 'science',
      title: 'Mock registry — the answers are made up',
      text: 'Runs call no registry and charge nothing. Approval decides, but writes nothing to any vendor record, and no acknowledgement can be requested. Set REGISTRY_MODE=surepass with a token to go live.',
    });
  }
  if (status && !status.pricesConfigured && !mock) {
    items.push({ key: 'prices', icon: 'payments', tone: 'amber', title: 'Prices are placeholders', text: 'The rate card is not set (VERIFICATION_PRICE_*_INR), so every cost shown here is an estimate, not what Surepass will invoice.' });
  }
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {items.map(({ key, ...n }) => (
        <Notice key={key} {...n} />
      ))}
    </div>
  );
}

/** A check named in a chip: "GST registration", with its icon. */
export function CheckChip({ checkKey, size = 'md' }) {
  const check = checkByKey(checkKey);
  if (!check) return null;
  const pad = size === 'sm' ? 'px-2 py-0.5 text-2xs' : 'px-2.5 py-1 text-[12px]';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border border-cos-line bg-white/70 font-semibold text-cos-ink ${pad}`}>
      <Icon name={check.icon} className={size === 'sm' ? 'text-[13px] text-cos-teal-deep' : 'text-[15px] text-cos-teal-deep'} />
      {check.label}
    </span>
  );
}

const CHECK_STATUS_PILL = {
  queued: 'pending',
  running: 'running',
  match: 'approved',
  changed: 'completed',
  attention: 'risk',
  not_found: 'failed',
  skipped: 'draft',
  failed: 'failed',
};

/** One check's verdict — "GST · Matches" — in the tool's pill vocabulary. */
export function CheckStatusPill({ checkKey, status, withName = true }) {
  const check = checkByKey(checkKey);
  const label = withName && check ? `${check.label} · ${CHECK_STATUS_LABEL[status] || status}` : CHECK_STATUS_LABEL[status] || status;
  return <StatusPill status={CHECK_STATUS_PILL[status] || 'draft'} label={label} />;
}

const OUTCOME_STYLE = {
  match: 'bg-[rgba(70,80,72,0.06)] text-cos-ink-muted',
  new: 'bg-cos-teal-faint text-cos-teal-deep',
  update: 'bg-cos-beige-light text-cos-beige-ink',
  check: 'bg-cos-status-coral-bg text-cos-status-coral',
};
const OUTCOME_ICON = { match: 'check', new: 'add', update: 'sync_alt', check: 'priority_high' };

/** A fact's outcome: Match · New · Will update · Check. */
export function OutcomePill({ outcome }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-2xs font-semibold ${OUTCOME_STYLE[outcome] || OUTCOME_STYLE.match}`}>
      <Icon name={OUTCOME_ICON[outcome] || 'check'} className="text-[12px]" weight={600} />
      {FACT_OUTCOME_LABEL[outcome] || outcome}
    </span>
  );
}

/** A compact stat tile, the way the campaign drawer shows its counts. */
export function Stat({ label, value, tone = 'ink', hint }) {
  const color = tone === 'teal' ? 'text-cos-teal-deep' : tone === 'coral' ? 'text-cos-status-coral' : tone === 'amber' ? 'text-cos-status-amber' : 'text-cos-ink';
  return (
    <div className="glass-inset px-3 py-2.5" title={hint}>
      <div className="text-2xs font-semibold uppercase tracking-wide text-cos-ink-muted">{label}</div>
      <div className={`mono mt-0.5 text-lg font-bold leading-none ${color}`}>{value}</div>
    </div>
  );
}
